import {
  CheckoutApplicationFieldType,
  type CheckoutFieldBlock,
} from "@/models/options";

import { formatAddress } from "./formatting";

import type { Address } from "@/models/Address";
import type { PhoneNumbers } from "@/models/applications";
import type {
  CheckoutDataField,
  CheckoutDataStepItem,
  CheckoutDataTable,
  CheckoutStep,
  CheckoutStepType,
} from "@/models/checkout";
import type { WorkflowFieldDefinition } from "@/models/workflows";

export const fieldDefinitionToField = (
  blockName: CheckoutFieldBlock["id"],
  fieldDefinition: WorkflowFieldDefinition
): CheckoutDataField => {
  return {
    id: crypto.randomUUID(),
    type: "application_field",
    name: fieldDefinition.id,
    label: fieldDefinition.name,
    block: blockName,
  };
};

export const checkoutBaseToDedicatedStep = <TStepType extends CheckoutStepType>(
  step: CheckoutStep | undefined,
  type: TStepType
): Extract<CheckoutStep, { type: TStepType }> | undefined => {
  if (step?.type !== type) {
    return undefined;
  }
  return step as Extract<CheckoutStep, { type: TStepType }>;
};

export const isIndividualField = (
  dataStepItem: CheckoutDataStepItem
): dataStepItem is CheckoutDataField =>
  !!dataStepItem.type &&
  ["application_field", "custom_field"].includes(dataStepItem.type);

export const isTable = (
  dataStepItem: CheckoutDataStepItem
): dataStepItem is CheckoutDataTable =>
  !!dataStepItem.type && ["table"].includes(dataStepItem.type);

export const getFormatterForDataField = (
  fieldType: CheckoutApplicationFieldType
) => {
  switch (fieldType) {
    case CheckoutApplicationFieldType.addresses:
      return checkoutAddressFormatter;
    case CheckoutApplicationFieldType.phoneNumbers:
      return checkoutPhoneFormatter;
    case CheckoutApplicationFieldType.email:
      return checkoutEmailFormatter;
    case CheckoutApplicationFieldType.ssnItin:
      return checkoutSsnItinFormatter;
    case CheckoutApplicationFieldType.phoneNumbersWithAdditionalPhoneNumber:
      return checkoutPhoneWithAdditionalPhonesFormatter;
    default:
      return null;
  }
};

const checkoutAddressFormatter = (value: Address[]) => {
  if (!value?.[0]) {
    return;
  }

  return formatAddress(value[0]);
};

const checkoutPhoneFormatter = (value: PhoneNumbers[]) => {
  if (!value?.[0]) {
    return;
  }

  return value[0]?.phone_number;
};

const checkoutEmailFormatter = (value: { email_address: string }) => {
  if (!value.email_address) {
    return "";
  }

  return value.email_address;
};

const checkoutSsnItinFormatter = (value: { ssn: string; itin: string }) => {
  if (!value) {
    return "";
  }

  return `SSN: ${value.ssn}, ITIN: ${value.itin}`;
};

const checkoutPhoneWithAdditionalPhonesFormatter = (value: {
  telephone: string;
}) => {
  if (!value?.telephone) {
    return "";
  }

  return value.telephone;
};
